import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getUserChatSummary, GetMessageData } from '../../chat/services/chat-data-provider';
import { getMergedChatSummaryItems } from '../../chat/utils/chat-summary-utils';
import { IChatData } from '../interfaces/IChatData';
import { MessageSummaryDto } from '../../chat/entities/chatMsg-summary-dto';
import { logout } from '../action/logout-action';

const initialState :IChatData={
  topics: null,
  chatSummary: null,
  error: null,
  isLoading: false,
  selectedTopic: null,
  unreadCount: 0,
}

export const getChatTopics = createAsyncThunk(
  'chat/getChatTopics',
  async () => {
      const request = {
        pageNumber: 1,
        pageSize: 50,
        searchQuery: '',
        status: []
      };
      const response = await getUserChatSummary(request);
      return getMergedChatSummaryItems(response.data.data);
  }
);

export const getChatSummary = createAsyncThunk<
  any[],  // Return type (an array of any)
  MessageSummaryDto,    // Argument type (the body you're passing)
  { rejectValue: string }
>(
  'chat/getChatSummary',
  async (body: MessageSummaryDto, { rejectWithValue }) => {
    try {
      const response = await GetMessageData(body);
      return response.data.data as any[];  // Ensure you're returning an array
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const chatSlice = createSlice({
  name: 'chat',
  initialState: initialState,
  reducers: { 
    StoreChatTopicList: (state, action: PayloadAction<IChatData>) => {         
      state.topics = action?.payload.topics;
    }, 
    updateChatTopicList: (state, action) => {   
      state.topics = action?.payload; 
    }, 
    StoreChatSummary: (state, action: PayloadAction<IChatData>) => {         
      state.chatSummary = action?.payload.chatSummary;
    },    
    appendChatSummary: (state, action) => {
      state.chatSummary = [...state.chatSummary, action.payload]; 
    },
    clearChatSummary: (state) => {
      // Concatenate the new topics to the existing topics array
      state.chatSummary = []; 
    },
    updateSelectedTopic: (state, action) => {   
      state.selectedTopic = action?.payload; 
    },
    incrementUnreadCount: (state) => {
      state.unreadCount += 1;
    },
    resetUnreadCount: (state) => {
      state.unreadCount = 0;
    },
    setUnreadCount: (state, action) => {
      state.unreadCount = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder    
      // Handling getChatTopics
      .addCase(getChatTopics.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getChatTopics.fulfilled, (state, action) => {
        state.isLoading = false;
        state.topics = action.payload;  // Assuming action.payload contains topics
      })
      .addCase(getChatTopics.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'An unknown error occurred';
      })
      // Handling getChatSummary
      .addCase(getChatSummary.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getChatSummary.fulfilled, (state, action) => {
        state.isLoading = false;
        state.chatSummary = action.payload;  // Assuming action.payload contains chat summary
      })
      .addCase(getChatSummary.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'An unknown error occurred';
      })
      // Handle logout
      .addCase(logout, () => initialState);
  },
});
export const { StoreChatTopicList,updateChatTopicList,StoreChatSummary
  ,appendChatSummary,clearChatSummary,updateSelectedTopic,incrementUnreadCount,resetUnreadCount,setUnreadCount } = chatSlice.actions
export default chatSlice.reducer;
